import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import config from "config.json";
import useFetch from "hooks/useFetch";
import ErrorModal from "components/Modal/errorModal";
interface FileUploadResponse {
  code: string;
  content: string[];
}

interface User {
  firstname: string;
  lastname: string;
  document: string;
  mail: string;
  id: number;
  phones: Phone[];
  semaphore_status: number;
  observations: string;
}

interface Phone {
  phone_number: string;
  active: boolean;
}

const AccountDocuments = () => {

  const LoadingSpinner = () => (
    <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-40">
      <div className="w-16 h-16 border-4 border-blue-500 border-t-transparent border-solid rounded-full animate-spin"></div>
    </div>
  ); 

  const [dniFile, setDniFile] = useState<File | null>(null);
  const [salaryFile, setSalaryFile] = useState<File | null>(null);
  const [previewDni, setPreviewDni] = useState<string | null>(null);
  const [previewSalary, setPreviewSalary] = useState<string | null>(null);
  const [isDniUploaded, setIsDniUploaded] = useState(false);
  const [isSalaryUploaded, setIsSalaryUploaded] = useState(false);
  const [isSalaryReUploaded, setIsSalaryReUploaded] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [userId, setUserId] = useState<number | null>(null);
  const [tokenFetched, setTokenFetched] = useState(false);
  const [showModal, setShowModal] = useState<boolean>(false);
  const [modalImage, setModalImage] = useState<string | null>(null);
  const [currentDocument, setCurrentDocument] = useState<string | null>(null);
  const [showErrorModal, setshowErrorModal] = useState(false)

  const navigate = useNavigate();

  const { data: tokenData, error: tokenError, isLoading: tokenIsLoading, fetchData: fetchToken } = useFetch<User>(
    "GET",
    `${config.backend_url}/authe/token`,
    {},
    { "Content-Type": "application/json", "SetiaAuthorization": `${sessionStorage.getItem("user_token")}` }
  );

  const getDocumentURL = (id_user: number, concept: string) => `${config.backend_url}/authe/get_documentation?id_user=${id_user}&concept=${concept}`;

  useEffect(() => {
    const fetchUserId = async () => {
      try {
        await fetchToken();
        if (tokenData && tokenData.id) {
          setUserId(tokenData.id);
          setTokenFetched(true);
        } else if (tokenError) {
          throw new Error("No se pudo obtener el ID del usuario.");
        }
      } catch (error) {
        console.error("Error al obtener la información del usuario:", error);
        setError("Hubo un error al obtener la información del usuario.");
      }
    };

    if (!tokenFetched && !tokenIsLoading) {
      fetchUserId();
    }
  }, [tokenError, tokenData, tokenIsLoading, fetchToken, tokenFetched]);

  useEffect(() => {
    const checkDocuments = async () => {
      if (userId) {
        try {
          const response = await fetch(`${config.backend_url}/authe/get_concepts?id_user=${userId}`, {
            headers: {
              "SetiaAuthorization": `${sessionStorage.getItem("user_token")}`,
            },
          });
          const data: FileUploadResponse = await response.json();

          setIsDniUploaded(data.content.includes("DNI"));
          setIsSalaryUploaded(data.content.includes("RECIBO"));

          if (data.content.includes("DNI") && data.content.includes("RECIBO") && tokenData?.semaphore_status === 1) {
            setMessage("La documentación está en proceso de validación.");
          }
        } catch (error) {
          console.error("Error al verificar los documentos del usuario:", error);
        }
      }
    };

    if (userId) {
      checkDocuments();
    }
  }, [userId]);

  const isFileSizeValid = (fileSizeInBytes: number | undefined) => {
    if (fileSizeInBytes) {
      const maxSizeInBytes = 16 * 1024 * 1024; // 16 MB en bytes
      return fileSizeInBytes < maxSizeInBytes;
    }
    return false
  };
  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>, type: string) => {
    const file = event.target.files?.[0];
    if (!isFileSizeValid(file?.size)){
      setshowErrorModal(true)
      return
    }
    if (file) {
      if (type === "dni") {
        setDniFile(file);
        setPreviewDni(URL.createObjectURL(file));
      } else if (type === "salary") {
        setSalaryFile(file);
        setPreviewSalary(URL.createObjectURL(file));
      }
    }
  };

  const uploadFile = async (file: File, concept: string) => {
    if (!userId) return;

    const formData = new FormData();
    formData.append("documentation", file);

    let url = `${config.backend_url}/authe/create_documentation?id_user=${userId}&concept=${concept}`;
    let method = "POST";

    // Si semaphore_status es 3, actualizar la documentación existente en lugar de crearla
    if (tokenData?.semaphore_status === 3 && concept === "DNI") {
      url = `${config.backend_url}/authe/update_documentation?id_user=${userId}&concept=${concept}`;
      method = "PUT"; // Cambiar el método a PUT para actualizar
    }

    const response = await fetch(url, {
      method: method,
      body: formData,
      headers: {
        "SetiaAuthorization": `${sessionStorage.getItem("user_token")}`,
      },
    });

    if (response.ok) {
      return true;
    } else {
      throw new Error("Error al subir el archivo");
    }
  };

  const handleFileUpload = async () => {
    setIsSubmitting(true);
    setMessage(null);
    setError(null);
    try {
      // Validar que ambos archivos estén seleccionados antes de enviar
      if (!dniFile || !salaryFile) {
        throw new Error("Debe subir ambos documentos (DNI y Recibo).");
      }

      // Subir DNI
      if (dniFile && (!isDniUploaded || tokenData?.semaphore_status === 3)) {
        await uploadFile(dniFile, "DNI");
        setIsDniUploaded(true);
      }

      // Subir Recibo
      if (salaryFile && (!isSalaryUploaded || tokenData?.semaphore_status === 3)) {
        await uploadFile(salaryFile, "RECIBO");
        setIsSalaryUploaded(true);
        setIsSalaryReUploaded(true);
      }

      // Mostrar mensaje de éxito
      setMessage("Archivos subidos exitosamente.");
      setError(null);

      // Recargar la página si todo salió bien
      window.location.reload();

    } catch (error) {
      console.error("Error al subir los archivos:", error);
      setError("Hubo un error al enviar los archivos.");
      setMessage(null);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleGoHome = () => {
    navigate("/account");
  };

  const showModalDocument = async (concept: string) => {
    try {
      if (!userId) return;

      const response = await fetch(getDocumentURL(userId, concept), {
        headers: {
          "SetiaAuthorization": `${sessionStorage.getItem("user_token")}`,
        },
      });

      if (response.ok) {
        const blob = await response.blob();
        const imageUrl = URL.createObjectURL(blob);
        setModalImage(imageUrl);
        setShowModal(true);
        setCurrentDocument(concept);
      } else {
        throw new Error("Error al obtener el documento");
      }
    } catch (error) {
      console.error("Error al obtener el documento:", error);
      setError("Hubo un error al obtener el documento.");
    }
  };

  const renderDocumentButtons = () => {
    if (!userId) return null;
    return (
      <div className="mb-6">
        {isDniUploaded && (
          <button
            onClick={() => showModalDocument("DNI")}
            className="w-full bg-blue-600 text-white py-2 px-4 rounded-full hover:bg-blue-800"
          >
            Ver DNI Subido
          </button>
        )}
        {isSalaryUploaded && (
          <button
            onClick={() => showModalDocument("RECIBO")}
            className="w-full bg-blue-600 text-white py-2 px-4 rounded-full hover:bg-blue-800 mt-4"
          >
            Ver Recibo Subido
          </button>
        )}
      </div>
    );
  };

  const renderStatusMessage = () => {
    if (tokenData?.semaphore_status === 1) {
      return (
        <div className="text-center text-green-600 mb-2">
          <p>Sus documentos están en proceso de validación.</p>
        </div>
      );
    }

    if (tokenData?.semaphore_status === 2) {
      return (
        <div className="text-center text-green-600 mb-2">
          <p>Sus documentos están aprobados. No necesita realizar acciones.</p>
        </div>
      );
    }

    if (tokenData?.semaphore_status === 3 && tokenData.observations) {
      return (
        <div className="text-center text-red-500 mb-2">
          <p>El administrador requiere que suba de nuevo sus documentos.</p>
          <p>{tokenData.observations}</p>
        </div>
      );
    }

    return null;
  };

  const renderDocumentUploadForm = () => {
    if (tokenData?.semaphore_status === 0 || tokenData?.semaphore_status === 3) {
      return (
        <>
          <div className="mb-6">
            <label className="block text-lg font-medium mb-2">Seleccione Foto DNI Frente:</label>
            {previewDni ? (
              <div className="mb-4">
                <img
                  src={previewDni}
                  alt="Vista previa DNI"
                  className="w-32 h-32 object-cover mx-auto rounded-md shadow-lg"
                />
                <button
                  className="mt-4 w-full bg-blue-600 text-white font-medium text-sm py-2 px-4 rounded-full cursor-pointer hover:bg-blue-800"
                  onClick={() => {
                    setDniFile(null);
                    setPreviewDni(null);
                  }}
                >
                  Reemplazar DNI
                </button>
              </div>
            ) : (
              <>
                <label
                  htmlFor="dni-upload"
                  className="w-full bg-blue-600 text-white font-medium text-sm py-2 px-4 rounded-full cursor-pointer hover:bg-blue-800"
                >
                  Seleccionar Archivo DNI
                </label>
                <input
                  id="dni-upload"
                  type="file"
                  className="hidden"
                  onChange={(e) => handleFileChange(e, "dni")}
                />
              </>
            )}
          </div>

          <div className="mb-6">
            <label className="block text-lg font-medium mb-2">Seleccione Foto Ultimo Recibo de Sueldo</label>
            {previewSalary ? (
              <div className="mb-4">
                <img
                  src={previewSalary}
                  alt="Vista previa Recibo"
                  className="w-32 h-32 object-cover mx-auto rounded-md shadow-lg"
                />
                <button
                  className="w-full bg-blue-600 text-white font-medium text-sm py-2 px-4 rounded-full cursor-pointer hover:bg-blue-800"
                  onClick={() => {
                    setSalaryFile(null);
                    setPreviewSalary(null);
                  }}
                >
                  Reemplazar Recibo de Sueldo
                </button>
              </div>
            ) : (
              <>
                <label
                  htmlFor="salary-upload"
                  className="w-full bg-blue-600 text-white font-medium text-sm py-2 px-4 rounded-full cursor-pointer hover:bg-blue-800"
                >
                  Seleccionar Archivo Recibo
                </label>
                <input
                  id="salary-upload"
                  type="file"
                  className="hidden"
                  onChange={(e) => handleFileChange(e, "salary")}
                />
              </>
            )}
          </div>

          <button
            className="w-full bg-blue-600 text-white py-3 px-4 rounded-full cursor-pointer hover:bg-blue-800"
            onClick={handleFileUpload}
            disabled={isSubmitting || (!dniFile || !salaryFile)}
          >
            {isSubmitting ? "Enviando..." : "Enviar"}
          </button>
        </>
      );
    }
    return null;
  };

  

  return (
    <>
    {isSubmitting && <LoadingSpinner />}
    <div className="container mx-auto mb-[150px] mt-[80px] p-4 max-w-xl bg-white shadow-md rounded-lg">
      <h1 className="text-2xl font-bold mb-6 text-center">Envio de Documentos</h1>
      {
        showErrorModal && 
        <ErrorModal message={"El archivo no puede superar los 16 MB"} isModalOpen={showErrorModal} handleModalClose={() => setshowErrorModal(false)} />
      }

      {error ? (
        <div className="text-red-600 text-center">
          <p className="mb-4">{error}</p>

          <button
            className="bg-blue-600 text-white py-3 px-4 rounded-full cursor-pointer hover:bg-blue-800"
            onClick={handleGoHome}
          >
            Ir a Mi Cuenta
          </button>
        </div>
      ) : (
        <>
          {message ? (
            <div className="text-green-600 text-center mb-2">
              <p className="mb-4">{message}</p>

            </div>
          ) : (
            <>
              {renderStatusMessage()}
              {renderDocumentButtons()}
              {renderDocumentUploadForm()}
            </>
          )}
        </>
      )}

      {error && <p className="mt-4 text-red-600 text-center">{error}</p>}

      <button
        className="mt-4 bg-blue-600 text-white py-3 px-4 rounded-full cursor-pointer hover:bg-blue-800 w-full"
        onClick={handleGoHome}
        disabled={isSubmitting}
      >
        Ir a la Home
      </button>

      {/* Modal para mostrar los documentos */}
      {showModal && (
        <div className="fixed inset-0 flex justify-center items-center bg-black bg-opacity-50 z-30">
          <div className="bg-white p-6 rounded-lg shadow-lg w-11/12 md:w-1/2 lg:w-1/3 max-h-[90vh] overflow-y-auto">
            <h2 className="text-xl font-bold mb-4 text-center">Documento: {currentDocument}</h2>
            {modalImage && (
              <img
                src={modalImage}
                alt="Documento"
                className="w-full h-auto object-contain mb-4 max-h-[50vh]" // Limitar la altura máxima de la imagen
              />
            )}
            <button
              className="w-full py-2 px-4 rounded-full text-white bg-blue-500"
              onClick={() => setShowModal(false)}
            >
              Cerrar
            </button>
          </div>
        </div>
      )}
    </div>
    </>
  );
};

export default AccountDocuments;
